import {
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  Separator,
  openDialog,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useUpdateNoteMutation} from '@dms/api/core';
import {
  useChangeOrderReturnVehicleMutation,
  useCreateRentalOrderReturnDocumentMutation,
  useGetRentalOrderQuery,
  useReturnRentalOrderVehicleMutation,
} from '@dms/api/rental';
import {useGetVehicleMileageQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetRentalVehicleQuery, useGetVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, useUpdateOdometer, useVatRatesOptions} from '@dms/shared';

import {Nullish, useRequiredParams} from 'shared/web';

import {RentalOrderNodeContext} from '../../../../constants/RentalOrderNodeContext';
import {AuthorizedPersons} from './components/AuthorizedPersons';
import {ConditionWhenReturning} from './components/ConditionWhenReturning';
import {Pricing} from './components/Pricing';
import {RentalNote} from './components/RentalNote';
import {RentAndReturnAddress} from './components/RentAndReturnAddress';
import {SelectTemplateAndSubmit} from './components/SelectTemplateAndSubmit';
import {useGetReturnVehicleFormDefaultValues} from './hooks/useGetReturnVehicleFormDefaultValues';
import {returnVehicleFormSchema, ReturnVehicleFormValues} from './types/ReturnVehicleFormValues';
import {getReturnVehicleRequestBody} from './utils/getReturnVehicleRequestBody';

export function ReturnVehicleForm() {
  const {id: rentalOrderId} = useRequiredParams();

  const {
    data: rentalOrder,
    isLoading: isLoadingRentalOrder,
    isError: isErrorRentalOrder,
  } = useGetRentalOrderQuery({rentalOrderId});

  const {data: vehicleMileage} = useGetVehicleMileageQuery(
    {vehicleId: rentalOrder?.rentalVehicleId ?? ''},
    {skip: isNil(rentalOrder?.rentalVehicleId)}
  );

  const [returnVehicle] = useReturnRentalOrderVehicleMutation();
  const [setReturnVehicle] = useChangeOrderReturnVehicleMutation();
  const [generateReturnDocument] = useCreateRentalOrderReturnDocumentMutation();
  const [addNote] = useUpdateNoteMutation();

  const [vatRates] = useVatRatesOptions();

  const {
    data: rentalVehicle,
    isLoading: isLoadingRentalVehicle,
    isError: isErrorRentalVehicle,
  } = useGetRentalVehicleQuery(
    {vehicleId: rentalOrder?.rentalVehicleId ?? ''},
    {
      skip: isNil(rentalOrder?.rentalVehicleId),
    }
  );

  const {
    data: vehicle,
    isLoading: isLoadingVehicle,
    isError: isErrorVehicle,
  } = useGetVehicleQuery(
    {vehicleId: rentalVehicle?.vehicleId ?? ''},
    {skip: isNil(rentalVehicle?.vehicleId)}
  );

  const [handleUpdateOdometer] = useUpdateOdometer(rentalVehicle?.vehicleId);
  const [submitType, setSubmitType] = useState<'returnAndGenerate' | 'saveChanges' | 'generate'>(
    'saveChanges'
  );
  const shouldReturnVehicle = submitType === 'returnAndGenerate';
  const shouldGenerateContract = submitType === 'generate' || submitType === 'returnAndGenerate';

  const {defaultValues, isLoading: areDefaultValuesLoading} = useGetReturnVehicleFormDefaultValues(
    rentalOrder,
    rentalVehicle
  );

  const updateNote = (note: string | Nullish, context: RentalOrderNodeContext) =>
    addNote({
      context,
      recordId: rentalOrderId,
      resourceId: EntityResourceIds.rentalOrder,
      updateNoteRequestBody: {
        text: note ?? '',
      },
    })
      .unwrap()
      .catch(handleApiError);

  const isLoading =
    isLoadingRentalOrder || isLoadingRentalVehicle || areDefaultValuesLoading || isLoadingVehicle;
  const isError = isErrorRentalOrder || isErrorRentalVehicle || isErrorVehicle;

  const handleSubmit: FormSubmitHandler<ReturnVehicleFormValues> = async (data) => {
    const changeOrderReturnVehicleRequestBody = getReturnVehicleRequestBody(
      data,
      vatRates,
      rentalOrder
    );

    await setReturnVehicle({
      rentalOrderId,
      changeOrderReturnVehicleRequestBody,
    })
      .unwrap()
      .then(async () => {
        updateNote(data.rentalOrderNote, RentalOrderNodeContext.returnVehicleNote);
        updateNote(data.conditionNote, RentalOrderNodeContext.returnVehicleConditionNote);

        if (shouldReturnVehicle) {
          await returnVehicle({
            rentalOrderId,
          }).unwrap();
        }

        if (shouldReturnVehicle || shouldGenerateContract) {
          await generateReturnDocument({
            rentalOrderId,
            templateId: data.templateId,
          }).unwrap();
        }
      })
      .then(() => {
        const isNewEntity = shouldReturnVehicle;

        const recordTitle = `${rentalOrder?.number} - ${vehicle?.title}`;

        if (isNewEntity || (vehicleMileage?.mileage ?? 0) < data.actualMileage) {
          handleUpdateOdometer({
            recordId: rentalOrderId,
            milage: data.actualMileage,
            action: 'car-rental-return',
            recordTitle,
          });
        }
      })
      .catch(handleApiError);
  };

  return (
    <Card title={i18n.t('page.rentalOrder.labels.rentVehicle')}>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
        <Form<ReturnVehicleFormValues>
          defaultValues={defaultValues}
          experimentalZodSchema={returnVehicleFormSchema}
          onSubmit={handleSubmit}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <AuthorizedPersons
                control={control}
                formApi={formApi}
                data-testid={testIds.rentalOrder.returnVehicle('RentAndReturnAddress')}
              />
              <RentAndReturnAddress
                control={control}
                formApi={formApi}
                data-testid={testIds.rentalOrder.returnVehicle('RentAndReturnAddress')}
              />
              <ConditionWhenReturning
                control={control}
                formApi={formApi}
                rentalOrder={rentalOrder}
                data-testid={testIds.rentalOrder.returnVehicle('ConditionWhenReturning')}
              />

              <Separator spacing={0} />

              <Pricing
                control={control}
                formApi={formApi}
                rentalVehicle={rentalVehicle}
                rentalOrder={rentalOrder}
                data-testid={testIds.rentalOrder.returnVehicle('pricing')}
              />

              <Separator spacing={0} />

              <RentalNote
                control={control}
                formApi={formApi}
                data-testid={testIds.rentalOrder.returnVehicle('rentalNote')}
              />

              <ButtonGroup align="right">
                <FormButton
                  control={control}
                  variant="secondary"
                  type="submit"
                  onClick={() => setSubmitType('saveChanges')}
                  title={i18n.t('general.actions.saveChanges')}
                />

                <FormButton
                  control={control}
                  onClick={() => {
                    openDialog(
                      <SelectTemplateAndSubmit
                        control={control}
                        data-testid={testIds.rentalOrder.returnVehicle('selectTemplateAndSubmit')}
                        onSubmit={() =>
                          setSubmitType(
                            rentalOrder?.state === 'rented' ? 'returnAndGenerate' : 'generate'
                          )
                        }
                      />,
                      {
                        title: i18n.t('page.rentalOrder.labels.returnContractTemplate'),
                        size: 'small',
                      }
                    );
                  }}
                  title={
                    rentalOrder?.state === 'rented'
                      ? i18n.t('page.rentalOrder.labels.returnAndGenerate')
                      : i18n.t('page.rentalOrder.labels.generateReturnDocument')
                  }
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </Card>
  );
}
